import { useState } from "react";
import { Link } from "react-router-dom";
import { useDocumentMeta } from "@/hooks/useDocumentMeta";
import { AtlasSection } from "@/components/atlas/AtlasSection";
import { AtlasLedger } from "@/components/atlas/AtlasLedger";
import { AtlasPillars } from "@/components/atlas/AtlasPillars";
import { AtlasProcess } from "@/components/atlas/AtlasProcess";
import { AtlasCtaBand } from "@/components/atlas/AtlasCtaBand";

const ATLAS_PATH = "/services/company-incorporation/jurisdiction-atlas";

// Decorative latitude/longitude grid, drawn in code (no third-party image).
const GraticuleBackdrop = () => (
  <svg
    aria-hidden="true"
    className="absolute inset-0 w-full h-full opacity-[0.08] pointer-events-none"
    preserveAspectRatio="xMidYMid slice"
    viewBox="0 0 1200 600"
  >
    {Array.from({ length: 13 }).map((_, i) => (
      <line key={`v${i}`} x1={i * 100} y1="0" x2={i * 100} y2="600" stroke="#FFFFFF" strokeWidth="1" />
    ))}
    {Array.from({ length: 7 }).map((_, i) => (
      <line key={`h${i}`} x1="0" y1={i * 100} x2="1200" y2={i * 100} stroke="#FFFFFF" strokeWidth="1" />
    ))}
    {Array.from({ length: 4 }).map((_, i) => (
      <ellipse key={`e${i}`} cx="600" cy="300" rx={120 + i * 150} ry="300" fill="none" stroke="#FFFFFF" strokeWidth="1" />
    ))}
  </svg>
);

export default function JurisdictionAtlasPage() {
  // Selection + corridor state live here so the ledger, map and panel stay in sync.
  const [selected, setSelected] = useState(null);
  const [corridor, setCorridor] = useState("All");

  useDocumentMeta({
    title: "Jurisdiction Atlas | AMADI Legal",
    rawTitle: true,
    description:
      "A DIFC-based legal team coordinating incorporation, governance and compliance across the Gulf, Africa and South Asia. Explore the jurisdictions we cover.",
    path: ATLAS_PATH,
  });

  return (
    <div data-testid="jurisdiction-atlas-page" className="pt-20">
      {/* Hero — Forest Green field, no photograph */}
      <section data-testid="atlas-hero" className="relative overflow-hidden bg-forest">
        <GraticuleBackdrop />
        <div className="relative max-w-[1400px] mx-auto px-6 lg:px-12 py-24 md:py-32">
          <h1 className="font-serif text-4xl sm:text-5xl lg:text-6xl leading-[1.05] text-white max-w-3xl">
            Set up where your business is heading.
          </h1>
          <span className="block h-px w-24 bg-[#C9A84C] mt-7" />
          <p className="mt-7 font-sans text-base md:text-lg leading-relaxed text-[#7A9E5A] max-w-2xl">
            A DIFC-based legal team coordinating incorporation, governance and compliance across the Gulf, Africa and South Asia.
          </p>

          <div className="mt-10 flex flex-wrap gap-4">
            <a
              href="#atlas"
              data-testid="atlas-hero-explore"
              onClick={(e) => {
                e.preventDefault();
                document.getElementById("atlas")?.scrollIntoView({ behavior: "smooth" });
              }}
              className="inline-flex items-center justify-center font-sans text-xs tracking-[0.14em] uppercase font-bold px-8 py-4 bg-[#B85C38] text-white border border-[#B85C38] transition-colors duration-300 hover:bg-[#9c4a2c] hover:border-[#9c4a2c]"
            >
              Explore the atlas
            </a>
            <Link
              to="/contact"
              data-testid="atlas-hero-contact"
              className="inline-flex items-center justify-center font-sans text-xs tracking-[0.14em] uppercase font-bold px-8 py-4 bg-transparent text-white border border-white/70 transition-colors duration-300 hover:bg-white hover:text-forest"
            >
              Talk to us
            </Link>
          </div>
        </div>
      </section>

      {/* Opening statement — warm off-white */}
      <section data-testid="atlas-opening" className="bg-[#F6F1E4] py-16 md:py-24">
        <div className="max-w-[1400px] mx-auto px-6 lg:px-12">
          <div className="max-w-3xl space-y-6">
            <p className="font-sans text-lg md:text-xl leading-relaxed text-charcoal/80">
              Every jurisdiction has its own company law, its own registry and its own habits. We map them so that a founder or investor sees the route before committing to it.
            </p>
            <p className="font-sans text-lg md:text-xl leading-relaxed text-charcoal/80">
              Select a location on the atlas to see how we can assist. Where local counsel is needed, we work alongside them.
            </p>
          </div>
        </div>
      </section>

      {/* Interactive atlas */}
      <AtlasSection
        selected={selected}
        onSelect={setSelected}
        dimCorridor={corridor === "All" ? null : corridor}
      />

      {/* The ledger */}
      <AtlasLedger corridor={corridor} onCorridorChange={setCorridor} onSelect={setSelected} />

      {/* Pillars + process + CTA */}
      <AtlasPillars />
      <AtlasProcess />
      <AtlasCtaBand />
    </div>
  );
}
